<template>
  <main>
    <Loader :loader="isFormLoading" :label="$t('labels.generic', { pt: 'Carregando...', en: 'Loading...' })"
    loaderType="progress" />
    <BreadcrumbForum v-if="isTeacherOrAdmin() && !isFormLoading"/>
    <header class="max-w-[1113px] custom-container-forum mx-auto" v-if="!isFormLoading">
      <button
        @click="redirectToTopicDetail"
        class="flex items-center gap-3 mt-6 text-base font-bold btn-simple"
      >
        <v-icon name="co-arrow-left" width="24" height="24" />
        <span>
          {{
            $t("labels.generic", {
              pt: "Voltar para tópico",
              en: "Go back to topic",
            })
          }}
        </span>
      </button>
      <h4 class="mt-8 text-2xl font-medium leading-8">
        {{ $t("forum.topic.comment.form.title") }}
      </h4>
    </header>
    <section class="flex justify-center flex-col items-center max-w-[1113px] mx-auto" v-if="!isFormLoading">
      <form action="" @submit.prevent="submitForm" class="mt-6">
        <div class="flex flex-col gap-2">
          <label> {{ $t("forum.topic.comment.form.label.comment") }}</label>
          <fieldset class="w-full">
            <div class="sm:w-sm md:w-md lg:w-[1008px] xl:w-[1113px]">
              <Editor
                v-model.lazy="comment.comment"
                class="mb-3 w-[95vw] sm:w-[615px] md:w-[752px] lg:w-[1008px] xl:w-[1113px]"
                :class="[
                  {
                    'border !border-red-600 rounded-md':
                      v$.comment.comment.$error,
                  },
                ]"
                editorStyle="height: 160px; max-width: inherit;"
              >
                <template v-slot:toolbar>
                  <div class="ql-formats">
                    <button v-tooltip.bottom="'Bold'" class="ql-bold"></button>
                    <button
                      v-tooltip.bottom="'Italic'"
                      class="ql-italic"
                    ></button>
                    <button
                      v-tooltip.bottom="'Underline'"
                      class="ql-underline"
                    ></button>
                  </div>

                  <div class="ql-formats">
                    <button
                      v-tooltip.bottom="'Ordered'"
                      class="ql-list"
                      value="ordered"
                    ></button>
                    <button
                      v-tooltip.bottom="'Bullet'"
                      class="ql-list"
                      value="bullet"
                    ></button>
                    <select class="ql-align" />
                  </div>

                  <div class="ql-formats">
                    <button
                      v-tooltip.bottom="'Link'"
                      class="ql-link"
                      value="link"
                    ></button>

                    <button
                      v-tooltip.bottom="'CodeBlock'"
                      class="ql-code-block"
                    ></button>
                  </div>
                </template>
              </Editor>
              <div>
                <Errors
                  :errors="v$.comment.comment.$errors"
                  :actualLength="comment?.comment?.length"
                  :maxLength="700"
                />
              </div>
            </div>
          </fieldset>
        </div>
        <Button
          type="button"
          @click="showEmbedImageComponent"
          v-if="!isImageBeingEmbedded"
          class="btn-simple  !p-0  !mb-4"
        >
          <v-icon name="hi-upload" class="mr-2" />
          {{
            $t("labels.generic", {
              pt: "Anexar imagem",
              en: "Attach image",
            })
          }}
        </Button>
        <AddFileInput
          class="mt-8"
          @onSetImage="setCommentImage"
          @onResetImage="resetCommentImage"
          :imageSelected="image"
          v-else
        />
        <span class="flex items-center gap-3">
          <button
            @click="redirectToTopicDetail"
            type="button"
            class="  btn-outline border border-h-primary-500 my-2 px-2 rounded flex items-center justify-center text-h-primary-500 font-bold w-[79px] h-8 box-border"
          >
            {{ $t("forum.topic.comment.form.buttons.cancel") }}
          </button>
          <Button
            type="submit"
            class="btn-primary transition-all  my-2 px-2 rounded flex items-center justify-center text-white font-bold w-[79px] h-[32px] box-border"
          >
            {{ $t("forum.topic.comment.form.buttons.publish") }}
          </Button>
        </span>
      </form>
    </section>
  </main>
</template>
<script lang="ts">
import { defineAsyncComponent, defineComponent } from "vue";
import { ForumService } from "../../forum.service";
import { ToastFactory } from "@/utils/toast-factory.util";
import { Messages } from "@/utils/messages.util";
import useVuelidate from "@vuelidate/core";
import { maxLength, required } from "@/locale/locale.config";
import { Comment, CommentForm } from "@/models/comment.model";
import { take } from "rxjs";
import { GenericsUtils } from "@/utils/generics.util";
import { getUserFromStorage } from "@/utils/user-store.util";
import { Roles } from "@/models/user.model";

const AddFileInput = defineAsyncComponent(
  () => import('@/views/CourseStudent/Forum/components/InputImageFile/input-image-file.vue')
);

const BreadcrumbForum = defineAsyncComponent(
  () => import('@/views/CourseStudent/Forum/components/BreadcrumbForum/breadcrumb-forum.vue')
);

export default defineComponent({
  validations() {
    return {
      comment: {
        comment: { required, max: maxLength(700) },
      },
    };
  },
  components: {
    AddFileInput,
    BreadcrumbForum
  },
  data() {
    return {
      comment: new Comment(),
      image: undefined as any,
      isImageBeingEmbedded: false,
      isFormLoading: true,
      params: {
        topicId: this.$route.params.topicId as string,
        commentId: this.$route.params.commentId as string,
        courseName: this.$route.params.courseName as string,
        id: this.$route.params.id as string,
      }
    };
  },
  setup() {
    return { v$: useVuelidate() };
  },
  methods: {
    submitForm(): void {
      this.v$.comment.$validate();
      if (!this.v$.comment.$error) {
        const formUpdateComment = new CommentForm();
        formUpdateComment.comment = this.comment.comment;
        formUpdateComment.mediaId = this.comment.media?.id;

        if (formUpdateComment.mediaId) {
          const appendName: any = {
            file: "file",
            data: "comment",
          };
          const commentForm = this.generics.fileBuild(
            formUpdateComment,
            null,
            appendName
          );
          this.editComment(commentForm);
        } else {
          const appendName: any = {
            file: "file",
            data: "comment",
          };
          const commentForm = this.generics.fileBuild(
            formUpdateComment,
            this.image,
            appendName
          );
          this.editComment(commentForm);
        }
      }
    },
    getCommentById(): void {
      this.service.comment.pipe(take(1)).subscribe({
        next: (response) => {
          this.comment = response;
          this.isFormLoading = false;
          if (this.comment.media) {
            this.isImageBeingEmbedded = true;
            this.image = this.comment.media;
          }
        },
        error: () => {
          this.redirectToTopicDetail();
        },
      });
      this.service.getCommentById(this.params.topicId, this.params.commentId);
    },
    editComment(commentForm: FormData): void {
      this.isFormLoading = true;
      this.service.comment.pipe(take(1)).subscribe({
        next: () => {
          this.$toast.add(ToastFactory.success(Messages.FORUM_COMMENT_UPDATED));
          this.redirectToTopicDetail();
        }, error: () => {
          this.isFormLoading = false;
        },
      });
      this.service.editComment(this.params.topicId, this.params.commentId, commentForm);
    },
    showEmbedImageComponent(): void {
      this.isImageBeingEmbedded = true;
    },
    setCommentImage(image: any): void {
      this.image = image;
      this.comment.media = undefined;
    },
    resetCommentImage(): void {
      this.image = undefined;
      this.comment.media = undefined;
    },
    redirectToTopicDetail(): void {
      this.$router.go(-1)
    },
    isTeacherOrAdmin(): boolean {
      const userRole = getUserFromStorage()?.role;
      return (userRole === Roles.ADMINISTRATOR || userRole === Roles.TEACHER);
    },
  },
  computed: {
    service(): ForumService {
      return new ForumService();
    },
    generics(): GenericsUtils {
      return new GenericsUtils();
    },
  },
  mounted() {
    this.getCommentById();
  },
});
</script>
